/**
 * Unifies Social Signals filtering and selection actions with the shared
 * two-row data-table action bar while preserving the page's existing behavior.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { COUNTRY_OPTIONS } from 'src/components/common/CampaignFilterBar';
import { DataTableActionBar } from 'src/components/common/data-table-action-bar/DataTableActionBar';

export interface SocialSignalsFilterState {
  country: string;
  sourcePlatform: string;
  topic: string;
  startDate: string;
  endDate: string;
}

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

export const EMPTY_SOCIAL_SIGNALS_FILTERS: SocialSignalsFilterState = {
  country: '',
  sourcePlatform: '',
  topic: '',
  startDate: daysAgo(6),
  endDate: today(),
};

const SOURCE_PLATFORM_OPTIONS = ['tiktok', 'google'];
const TOPIC_OPTIONS = ['entertainment', 'sports', 'technology', 'health', 'business', 'politics', 'lifestyle'];

export interface SocialSignalsActionBarProps {
  filters: SocialSignalsFilterState;
  onFiltersChange: (filters: SocialSignalsFilterState) => void;
  search: string;
  onSearchChange: (search: string) => void;
  selectedCount: number;
  onDownloadCsv: () => void;
  onClearSelection: () => void;
}

const getPlatformLabel = (platform: string) =>
  platform === 'tiktok' ? 'TikTok' : 'Google';

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const SocialSignalsActionBar: React.FC<SocialSignalsActionBarProps> = ({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  selectedCount,
  onDownloadCsv,
  onClearSelection,
}) => {
  const { t } = useTranslation();
  const [localSearch, setLocalSearch] = useState(search);
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) onSearchChangeRef.current(localSearch);
    }, 400);
    return () => { clearTimeout(timer); };
  }, [localSearch, search]);

  const hasActiveFilters = filters.country !== '' ||
    filters.sourcePlatform !== '' ||
    filters.topic !== '' ||
    filters.startDate !== EMPTY_SOCIAL_SIGNALS_FILTERS.startDate ||
    filters.endDate !== EMPTY_SOCIAL_SIGNALS_FILTERS.endDate;

  const handleClearAll = useCallback(() => {
    onFiltersChange(EMPTY_SOCIAL_SIGNALS_FILTERS);
  }, [onFiltersChange]);

  return (
    <DataTableActionBar
      bulkContent={selectedCount > 0
        ? (
        <>
          <Text data-testid="social-signals-selected-count" type="b200">
            {t('social_signals_selected_count', { count: String(selectedCount) })}
          </Text>
          <Button
            data-testid="social-signals-download-csv"
            type="link"
            onClick={onDownloadCsv}
          >
            {t('download_csv')}
          </Button>
          <Button
            data-testid="social-signals-clear-selection"
            type="link"
            onClick={onClearSelection}
          >
            {t('social_signals_clear')}
          </Button>
        </>
          )
        : (
        <Text color="secondary" type="b300">
          {t('select_row_to_view_bulk_actions')}
        </Text>
          )}
      filterButtonLabel={t('social_signals_filters')}
      filterButtonTestId="social-signals-filter-button"
      filterContent={hasActiveFilters
        ? (
        <>
          {filters.country && (
            <Tag
              data-testid="social-signals-filter-tag-country"
              type="theme"
              onClose={() => { onFiltersChange({ ...filters, country: '' }); }}
            >
              {`${t('country')}: ${filters.country}`}
            </Tag>
          )}
          {filters.sourcePlatform && (
            <Tag
              data-testid="social-signals-filter-tag-source-platform"
              type="theme"
              onClose={() => { onFiltersChange({ ...filters, sourcePlatform: '' }); }}
            >
              {`${t('social_signals_col_source_platform')}: ${getPlatformLabel(filters.sourcePlatform)}`}
            </Tag>
          )}
          {filters.topic && (
            <Tag
              data-testid="social-signals-filter-tag-topic"
              type="theme"
              onClose={() => { onFiltersChange({ ...filters, topic: '' }); }}
            >
              {`${t('social_signals_col_topic')}: ${capitalize(filters.topic)}`}
            </Tag>
          )}
          {(filters.startDate !== EMPTY_SOCIAL_SIGNALS_FILTERS.startDate ||
            filters.endDate !== EMPTY_SOCIAL_SIGNALS_FILTERS.endDate) && (
            <Tag
              data-testid="social-signals-filter-tag-date-range"
              type="theme"
              onClose={() => {
                onFiltersChange({
                  ...filters,
                  startDate: EMPTY_SOCIAL_SIGNALS_FILTERS.startDate,
                  endDate: EMPTY_SOCIAL_SIGNALS_FILTERS.endDate,
                });
              }}
            >
              {`${t('social_signals_date_range')}: ${filters.startDate} – ${filters.endDate}`}
            </Tag>
          )}
          <Button
            data-testid="social-signals-clear-filters"
            type="link"
            onClick={handleClearAll}
          >
            {t('clear_filters')}
          </Button>
        </>
          )
        : (
        <Text color="secondary" type="b300">
          {t('no_filters_applied')}
        </Text>
          )}
      filterPopoverContent={(
        <>
          <Select
            data-testid="social-signals-filter-country"
            label={t('country')}
            placeholder={t('select_country')}
            size="small"
            value={filters.country}
            width={200}
            onChange={(country: string) => {
              onFiltersChange({ ...filters, country });
            }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {COUNTRY_OPTIONS.map((country) => (
              <SelectOption key={country} label={country} value={country} />
            ))}
          </Select>
          <Select
            data-testid="social-signals-filter-source-platform"
            label={t('social_signals_col_source_platform')}
            placeholder={t('all')}
            size="small"
            value={filters.sourcePlatform}
            width={200}
            onChange={(sourcePlatform: string) => {
              onFiltersChange({ ...filters, sourcePlatform });
            }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {SOURCE_PLATFORM_OPTIONS.map((platform) => (
              <SelectOption
                key={platform}
                label={getPlatformLabel(platform)}
                value={platform}
              />
            ))}
          </Select>
          <Select
            data-testid="social-signals-filter-topic"
            label={t('social_signals_col_topic')}
            placeholder={t('all')}
            size="small"
            value={filters.topic}
            width={200}
            onChange={(topic: string) => {
              onFiltersChange({ ...filters, topic });
            }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {TOPIC_OPTIONS.map((topic) => (
              <SelectOption key={topic} label={capitalize(topic)} value={topic} />
            ))}
          </Select>
          <DateRangePicker
            allowSameStartAndEnd={true}
            data-testid="social-signals-filter-date-range"
            disabledDates={(date: string) => date > today() || date < daysAgo(6)}
            endLabel={t('end')}
            size="small"
            startLabel={t('start')}
            value={[filters.startDate, filters.endDate]}
            width={400}
            onChange={(value: string[]) => {
              if (value[0] && value[1]) {
                onFiltersChange({
                  ...filters,
                  startDate: value[0],
                  endDate: value[1],
                });
              }
            }}
          />
        </>
      )}
      search={{
        value: localSearch,
        placeholder: t('social_signals_search_trends'),
        testId: 'social-signals-search-trends',
        width: 250,
        onChange: setLocalSearch,
        onSubmit: () => { onSearchChangeRef.current(localSearch); },
      }}
      onClearFilters={handleClearAll}
    />
  );
};
